import React, { useState } from 'react';

export default function ThinkyIOTSCADA({ onBack }) {
  return (
    <div className="min-h-screen bg-black text-white px-4 py-8">
      <button onClick={onBack} className="w-24 mb-2 px-4 py-2 bg-blue-600 rounded hover:bg-blue-500">&larr; Back</button>
      <header className="space-y-2 flex flex-col justify-center">
        <h1 className="text-lg lg:text-xl font-bold">
          🎛️ Think5Whys-Powered SCADA Intelligence
        </h1>
        <p className="mb-1 text-blue-300 text-sm lg:text-[14px] text-center">Modern Supervisory Control and Data Acquisition.</p>
        <p className="text-gray-300 text-xs lg:text-sm text-center">
          Transforming SCADA from a monitoring system into a thinking system —
          enabling contextual reasoning, root-cause analysis and faster operational decisions.
        </p>
      </header>

      <section className="bg-black text-white px-4 py-4 space-y-4">
        <div className="space-y-4">
          <h2 className="text-sm lg:text-lg font-semibold">The Evolution of SCADA Systems</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
            <li>
              <strong>Traditional SCADA:</strong> Basic monitoring, alarms, and manual control.
            </li>
            <li>
              <strong>High-Performance SCADA:</strong> Minimalist interfaces optimized for fast human response.
            </li>
            <li>
              <strong>Think5Whys SCADA:</strong> Context-aware intelligence that explains <em>why</em> events occur, not just <em>what</em>.
            </li>
          </ul>
        </div>
        <div className="space-y-4">
          <h2 className="text-sm lg:text-lg font-semibold">Why SCADA Needs Intelligence</h2>
          <p className="text-gray-300 text-sm lg:text-[14px]">
            Modern IIoT and MSME environments generate massive telemetry, but data alone does not
            deliver decisions. Operators face alarm fatigue, repeated failures, and dependency
            on tribal knowledge.
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
            <li>Repeated alarms without root-cause clarity</li>
            <li>Operator overload and slow response loops</li>
            <li>Manual interpretation of complex system behavior</li>
            <li>Reactive operations instead of predictive actions</li>
          </ul>
        </div>
        <div className="space-y-4">
          <h2 className="text-sm lg:text-lg font-semibold">Reference Use Case: Intelligent Process Control</h2>
          <p className="text-gray-300 text-sm lg:text-[14px]">
            A typical process-controlled system such as water treatment or swimming pool management
            includes pumps, valves, filters, tanks, sensors, and gateways. ThinkyIOT visualizes
            these components using industrial SCADA symbols while reasoning across their behavior.
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
            <li>Pumps, motors, and heaters</li>
            <li>Flow control valves and filters</li>
            <li>Tank level, temperature, pressure, and pH sensors</li>
            <li>Edge gateways and control units</li>
          </ul>
        </div>
        <div className="space-y-4">
          <h2 className="text-sm lg:text-lg font-semibold">What Makes Think5Whys Different</h2>
          <p className="text-gray-300 text-sm lg:text-[14px]">
            Traditional SCADA reports states. Think5Whys connects causes, consequences, and intent.
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
            <li>Explains causal chains behind anomalies</li>
            <li>Correlates multiple signals into one insight</li>
            <li>Reduces noise by prioritizing impact</li>
            <li>Supports faster, confident decisions</li>
          </ul>
        </div>
        <div className="space-y-6">
          <h2 className="text-sm lg:text-lg font-semibold">ThinkyIOT SCADA Intelligence Architecture</h2>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-2'>
            <div className='flex flex-col'>
              <h3 className="text-sm lg:text-[14px] font-semibold mb-1">1. Control Systems & Edge Integration</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
                <li>PLCs, RTUs, embedded controllers</li>
                <li>Industrial protocols including Modbus, OPC-UA, BACnet</li>
                <li>Brownfield and greenfield compatibility</li>
              </ul>
            </div>

            <div className='flex flex-col'>
              <h3 className="text-sm lg:text-[14px] font-semibold mb-1">2. Edge & Gateway Intelligence</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
                <li>Local buffering during connectivity loss</li>
                <li>Secure protocol translation</li>
                <li>Edge-level continuity for critical operations</li>
              </ul>
            </div>

            <div className='flex flex-col'>
              <h3 className="text-sm lg:text-[14px] font-semibold mb-1">3. Think5Whys Reasoning Layer</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
                <li>Real-time data transformations</li>
                <li>Behavioral pattern recognition</li>
                <li>Causal reasoning and decision chains</li>
              </ul>
            </div>

            <div className='flex flex-col'>
              <h3 className="text-sm lg:text-[14px] font-semibold mb-1">4. Intelligent Alarms & Notifications</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
                <li>Alarm correlation and lifecycle tracking</li>
                <li>Root-cause-aware escalation</li>
                <li>Context-rich notifications across channels</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h2 className="text-sm lg:text-lg font-semibold">SCADA Dashboards with Thinking Capability</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm lg:text-[14px]">
            <li>Industrial SCADA symbols and widgets</li>
            <li>Real-time command and control</li>
            <li>Situation-driven highlighting</li>
            <li>Predictive and contextual indicators</li>
          </ul>
        </div>
        <div className="space-y-4">
          <h2 className="text-sm lg:text-lg font-semibold">Industrial Applications</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-300 list-disc list-inside  text-sm lg:text-[14px]">
            <li>Water and wastewater management</li>
            <li>Food and beverage processing</li>
            <li>Energy, heating, and HVAC systems</li>
            <li>Oil and gas operations</li>
            <li>Chemical processing plants</li>
            <li>MSME manufacturing environments</li>
          </ul>
        </div>
        <footer className="border-t border-neutral-800 p-2">
          <p className="text-gray-300 text-sm lg:text-[14px]">
            <strong>ThinkyIOT with Think5Whys</strong> moves SCADA from reactive monitoring to reflective,
            intelligence-driven operations — because the future of SCADA is not more data,
            but better thinking.
          </p>
        </footer>
      </section>
    </div>
  );
}
